import { useEffect } from 'react'
import { Button, Form, Space, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { ClientThemeColorFields } from '@/features/client/components/ClientThemeColorFields'
import { DEFAULT_CLIENT_TEXT_COLOR } from '@/features/client/clientFormShared'
import {
  useAdvisoryAiOrgs,
  useAdvisoryAiThemeColors,
  useUpdateAdvisoryAiThemeColorsMutation,
} from '@/features/advisory-ai/hooks'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import { PdfReportPreview } from './PdfReportPreview'
import { PptReportPreview } from './PptReportPreview'
import { VideoReportPreview } from './VideoReportPreview'

type ThemeColorsPanelProps = {
  clientId: string
}

type ThemeColorsFormValues = {
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

export function ThemeColorsPanel({ clientId }: ThemeColorsPanelProps) {
  const { addToast } = useToast()
  const session = useAuthSession()
  const themeColorsQuery = useAdvisoryAiThemeColors(clientId)
  const orgsQuery = useAdvisoryAiOrgs({ clientId })
  const updateMutation = useUpdateAdvisoryAiThemeColorsMutation(clientId)
  const [form] = Form.useForm<ThemeColorsFormValues>()
  // Gated on user type rather than on `updateAdvisoryAI`, which is what the
  // rest of this feature keys on. Same pair as dashboard-builder's
  // canManageClientMemory; note the client record's own colour fields are not
  // gated this way, so this restriction is not the only path to them.
  const canEdit =
    session?.currentUserTypeId === UserTypes.Admin ||
    session?.currentUserTypeId === UserTypes.ClientAdmin
  const firstOrg = orgsQuery.data?.[0]
  const orgName = firstOrg?.companyName?.trim() || firstOrg?.name?.trim() || undefined

  useEffect(() => {
    const data = themeColorsQuery.data
    if (!data) return
    form.setFieldsValue({
      primaryColor: data.primaryColor?.trim() || undefined,
      secondaryColor: data.secondaryColor?.trim() || undefined,
      textColor: data.textColor?.trim() || DEFAULT_CLIENT_TEXT_COLOR,
    })
  }, [themeColorsQuery.data, form])

  const handleSubmit = async (values: ThemeColorsFormValues) => {
    // Hiding Save is presentation; this is the rule. Without it the only thing
    // stopping a read-only save is that nothing currently submits the form.
    if (!canEdit) return
    try {
      await updateMutation.mutateAsync({
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        textColor: values.textColor,
      })
      addToast('Theme colors updated', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save', 'error')
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden max-w-3xl">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/70">
        <Space>
          <span className="font-medium text-gray-900">Theme Colours</span>
          <Tooltip title="Branding colours used across AdvicePrep outputs">
            <InfoCircleOutlined className="text-gray-400" />
          </Tooltip>
        </Space>
      </div>

      {themeColorsQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : themeColorsQuery.error ? (
        <div className="m-5 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {themeColorsQuery.error instanceof Error ? themeColorsQuery.error.message : 'Failed to load theme colors'}
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          className="p-5"
          disabled={!canEdit}
        >
          <ClientThemeColorFields />

          <PdfReportPreview
            form={form}
            clientName={session?.currentClientName}
            orgName={orgName}
          />

          <PptReportPreview
            form={form}
            clientName={session?.currentClientName}
            orgName={orgName}
          />

          <VideoReportPreview form={form} clientName={session?.currentClientName} />

          {canEdit && (
            <div className="flex justify-end gap-2 mt-4">
              <Button
                className="min-w-20"
                onClick={() => {
                  const data = themeColorsQuery.data
                  form.setFieldsValue({
                    primaryColor: data?.primaryColor?.trim() || undefined,
                    secondaryColor: data?.secondaryColor?.trim() || undefined,
                    textColor: data?.textColor?.trim() || DEFAULT_CLIENT_TEXT_COLOR,
                  })
                }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                className="feature-button-solid"
                htmlType="submit"
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            </div>
          )}
        </Form>
      )}
    </div>
  )
}
